import {
	CloseOutlined,
	ExportOutlined,
	InboxOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { payrollApi } from 'Api/payroll';
import { viewPayrollApi } from 'Api/payrollView';
import {
	Button,
	Col,
	Divider,
	Form,
	Modal,
	Row,
	Table,
	Tag,
	Tooltip,
	Upload,
	UploadFile,
} from 'antd';
import Title from 'antd/es/typography/Title';
import { UploadChangeParam } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import Buttons from 'components/Global/Buttons';
import InputNumberField from 'components/Global/InputNumberField';
import SelectDropdown from 'components/Global/SelectDropdown';
import TextareaFieldInput from 'components/Global/textareaField';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DownloadSvg } from 'utils/svgs';
import {
	capitalizeWords,
	formatNumber,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import GlobalHeader from '../../GlobalHeader';
import styles from './index.module.scss';
import './index.scss';

type Props = {
	selectedPayrollId: string;
	onlyView: boolean;
	setIsUniversalLoading: any;
	headerData: any;
	payPeriodId: any;
	year: number;
	showSteps: boolean;
};

const TotalEarningTable: React.FC<Props> = (props: Props) => {
	const {
		selectedPayrollId,
		onlyView,
		setIsUniversalLoading,
		headerData,
		payPeriodId,
		year,
		showSteps,
	} = props;

	const [earningsData, setEarningsData] = useState<any[]>([]);
	const [isEarningsLoading, setEarningsLoading] = useState(false);
	const [columns, setColumns] = useState<any>([]);
	const [rowData, setRowData] = useState<any[]>([]);
	const { data } = useSelector((state: any) => state?.userProfile);
	const [page, setPage] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [pageSize, setPageSize] = useState(31);
	const [earningsOptions, setEarningsOptions] = useState<any>({});
	const [payrollSpecificData, setPayrollSpecificData] = useState<any>({});

	const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);

	const [earningDetails, setEarningDetails] = useState<any>({
		employeeId: '',
		amount: null,
		notes: '',
		earningType: '',
	});

	const [formError, setFormError] = useState<any>({
		employeeId: false,
		amount: false,
		notes: false,
		earningType: false,
	});

	const [isLoading, setIsLoading] = useState(false);

	const [isAddEarningModal, setIsAddEarningModal] = useState(false);
	const [isEarningOptionsLoading, setIsEarningOptionsLoading] = useState(false);

	// Upload Earnings Modal states
	const [isUploadEarningsModalVisible, setIsUploadEarningsModalVisible] =
		useState(false);
	const [file, setFile] = useState<any>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isUploadLoading, setIsUploadLoading] = useState(false);
	const [importResults, setImportResults] = useState<any>(null);
	const [showResults, setShowResults] = useState(false);
	let isRemoving = false;

	const tableChangeHandler = (pagination: any, filters: any, sorter: any) => {
		setTotalRecords(pagination.total);
		setPage(pagination.current);
		setPageSize(pagination.pageSize);
	};

	const { conversionRate, usdCurrencyToggle, isUsdCurrency } = useSelector(
		(state: any) => state.usdCurrencyToggle
	);
	const closeEarningModal = () => {
		setIsAddEarningModal(false);
		setIsEarningOptionsLoading(false);
		setFormError({
			employeeId: false,
			amount: false,
			notes: false,
			earningType: false,
		});
		setEarningDetails({
			employeeId: '',
			amount: null,
			notes: '',
			earningType: '',
		});
	};

	const myButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: isLoading,
			isSubmit: false,
			onclick: () => {
				closeEarningModal();
			},
		},
		{
			text: 'Save',
			isLoading: isLoading,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			onclick: () => {
				handleSubmit();
			},
		},
	];

	const handleSubmit = async () => {
		let checkFormError = validateFormData(
			{ ...earningDetails },
			{ ...formError }
		);

		checkFormError = {
			...checkFormError,
		};
		setFormError(checkFormError);
		if (hasFormError(checkFormError)) {
			return;
		}
		try {
			setIsLoading(true);
			const data = {
				employeeId: earningDetails.employeeId,
				payrollId: selectedPayrollId,
				earningId: earningDetails.earningType,
				amount: earningDetails.amount,
				reason: earningDetails.notes,
			};
			const apiResponse = await payrollApi.createEmployeeSpecificEarnings(
				data
			);
			const isUpdated = apiResponse.data.data.isUpdated ?? false;
			const employeeName = employeeOptions.find(
				(emp) => emp.value === data.employeeId
			).fullName;
			toastText(
				`Earning successfully ${
					isUpdated ? 'updated' : 'added'
				} for ${employeeName}`,
				'success'
			);
			closeEarningModal();
			fetchEarnings(selectedPayrollId);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in adding new earning';

			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const formatData = (rows: any) => {
		const formattedRowData = rows?.map((item: any) => {
			const formattedItem: any = {
				__raw__: item,
			};

			for (const key in item) {
				if (key == 'Employee') {
					formattedItem[key] = item[key];
				} else if (!usdCurrencyToggle && isUsdCurrency) {
					formattedItem[key] = formatNumber(
						Number(item[key]),
						data?.CompanyCurrencies,
						true
					);
				} else {
					formattedItem[key] = formatNumber(
						Number(item[key] * (conversionRate ?? 1)),
						data?.CompanyCurrencies
					);
				}
			}

			return formattedItem;
		});
		setEarningsData(formattedRowData);
	};

	const generateColumns = (
		columnsData: any,
		payrollSpecificDataParam?: any
	) => {
		// Use parameter first, fallback to state
		const payrollSpecificToUse =
			payrollSpecificDataParam || payrollSpecificData || {};
		const flattenedColumns: any[] = [];

		// Add Employee column first
		flattenedColumns.push({
			title: 'Employees',
			dataIndex: 'Employee',
			key: 'Employee',
			className: 'bg-white',
			fixed: 'left',
			width: 250,
			render: (text: string, record: any) => {
				return (
					<span
						style={{
							cursor: 'default',
							display: 'flex',
							alignItems: 'center',
							gap: '4px',
						}}
					>
						{text}
					</span>
				);
			},
		});

		// Add all earning columns with payroll-specific indicators
		columnsData.forEach((column: any) => {
			const { title } = column;
			flattenedColumns.push({
				id: Math.random(),
				title: capitalizeWords(title),
				dataIndex: title,
				key: title,
				width: '200px',
				className: 'bg-white',
				render: (value: any, record: any) => {
					const raw = record.__raw__;

					const isTotalRow = raw?.Employee === 'Total';

					if (isTotalRow) {
						return <span>{value}</span>;
					}

					const employeeId = raw?.employeeId;
					const payrollSpecific =
						payrollSpecificToUse?.[employeeId]?.[title];

					const isPayrollSpecific =
						payrollSpecific?.isPayrollSpecific;
					if (isPayrollSpecific) {
						const { reason } = payrollSpecific;

						let tooltipContent = (
							<div style={{ maxWidth: '250px' }}>
								<div style={{ marginBottom: '4px' }}>
									<span>Reason : </span>{' '}
									{!invalidText(reason)
										? reason
										: 'added for this payroll'}
								</div>
							</div>
						);

						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
								}}
							>
								<span>{value}</span>
								<Tooltip title={tooltipContent} placement="top">
									<InfoCircleOutlined
										style={{
											color: '#584495',
											fontSize: '14px',
										}}
									/>
								</Tooltip>
							</div>
						);
					}

					return <span>{value}</span>;
				},
			});
		});

		// Add Total column at the end
		flattenedColumns.push({
			title: 'Total',
			dataIndex: 'total',
			key: 'total',
			width: '150px',
			className: 'bg-white',
		});

		return flattenedColumns;
	};

	const fetchEarnings = async (payRollId: string) => {
		try {
			let response;
			setEarningsLoading(true);

			setIsUniversalLoading(true);
			if (onlyView) {
				response = await viewPayrollApi.getAllEmployeeEarnings({
					payrollId: payRollId,
					page: page,
					pageSize: pageSize - 1,
				});
			} else {
				response = await payrollApi.getPayrollEarnings({
					payrollId: payRollId,
					page: page,
					pageSize: pageSize - 1,
				});
			}
			const { columns, rows } = response?.data?.data;

			const payrollSpecific =
				response?.data?.data?.payrollSpecificData || {};
			setPayrollSpecificData(payrollSpecific);

			const generatedColumns = generateColumns(columns, payrollSpecific);

			// Employee and earnings options are now fetched on-demand when modal opens
			setTotalRecords(response?.data?.data?.totalRecords);
			setRowData(rows);
			formatData(rows);
			setColumns(generatedColumns);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching earnings';

			toastText(errorMessage, 'error');
		} finally {
			setEarningsLoading(false);
			setIsUniversalLoading(false);
		}
	};

	// Fetch earning options on-demand when modal opens
	const fetchEarningOptions = async (payrollId: string) => {
		try {
			setIsEarningOptionsLoading(true);
			const response = await payrollApi.getEmployeeEarningOptions(payrollId);
			setEmployeeOptions(response?.data?.data?.payrollEmployeeOptions);
			setEarningsOptions(response?.data?.data?.masterEarningsOptions);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Failed to load earning options';
			toastText(errorMessage, 'error');
		} finally {
			setIsEarningOptionsLoading(false);
		}
	};

	useEffect(() => {
		if (selectedPayrollId) {
			fetchEarnings(selectedPayrollId);
		}
	}, [selectedPayrollId, page, pageSize]);

	useEffect(() => {
		formatData(rowData);
	}, [usdCurrencyToggle]);

	const handleChange = (
		value: string | number | null,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		OnChange(value, name);
	};

	const OnChange = (value: string | number | null, key: string) => {
		setEarningDetails((prev: any) => {
			if (key === 'employeeId') {
				return {
					employeeId: value, // set new employee
					earningType: null, // reset earning type
					amount: null, // reset amount
				};
			}

			return {
				...prev,
				[key]: value,
			};
		});

		const checkFormError = validateFormData(
			{ [key]: value },
			{ ...formError }
		);
		setFormError(checkFormError);
	};

	// Download template function
	const downloadTemplate = async () => {
		try {
			const response = await payrollApi.downloadEarningsTemplate(
				selectedPayrollId
			);
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
			);
			const link = document.createElement('a');
			link.href = url;
			link.download = `earnings_template.xlsx`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			toastText('Template downloaded successfully', 'success');
		} catch (error) {
			toastText('Failed to download template', 'error');
		}
	};

	const handleUpload = async (file: any) => {
		try {
			setIsUploadLoading(true);

			// Only proceed if file is xlsx
			let formData = new FormData();
			formData.append('documentName', 'earnings');
			formData.append('file', file);
			formData.append('payrollId', selectedPayrollId);

			const validatedData: any = await payrollApi.uploadEarningsFile(
				formData
			);
			const apiResponseData = validatedData.data.data;

			setImportResults(apiResponseData);
			setShowResults(true);
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				`Failed to process file. Please try again`;
			toastText(message, 'error');
			setFileList([]);
		} finally {
			setIsUploadLoading(false);
		}
	};

	const resetImport = () => {
		setImportResults(null);
		setShowResults(false);
		setFile(null);
		setFileList([]);
	};

	// Function to close and refresh
	const closeAndRefresh = () => {
		if (importResults?.summary?.successfulRows > 0) {
			// Refresh the earnings table
			fetchEarnings(selectedPayrollId);
		}
		cancelUploadEarningsModal();
	};

	const renderModalContent = () => {
		if (showResults && importResults) {
			const { summary, errors } = importResults;
			const hasSuccess = summary.successfulRows > 0;
			const hasErrors = summary.errorRows > 0;

			return (
				<div className={`${styles['modal']} modal`}>
					<div className={styles['modal-header']}>
						<Title
							level={4}
							style={{ color: '#584495', margin: 0 }}
						>
							{hasSuccess && !hasErrors && '🎉 Import Successful'}
							{hasSuccess &&
								hasErrors &&
								'⚠️ Import Completed with Errors'}
							{!hasSuccess && hasErrors && '❌ Import Failed'}
						</Title>
						<div
							className={styles['close-icon']}
							onClick={closeAndRefresh}
						>
							<CloseOutlined />
						</div>
					</div>

					{/* Summary Section */}
					<div
						style={{
							padding: '16px',
							backgroundColor: '#fafafa',
							borderLeft: `4px solid ${
								hasErrors ? '#ff4d4f' : '#52c41a'
							}`,
							borderRadius: '6px',
							marginBottom: '16px',
						}}
					>
						<Title
							level={5}
							style={{ margin: 0, color: '#584495' }}
						>
							Import Summary
						</Title>
						<div
							style={{
								display: 'flex',
								gap: '16px',
								marginTop: '8px',
							}}
						>
							{hasSuccess && (
								<Tag
									style={{
										background: '#f5f5f5',
										color: '#009049',
										border: 'none',
									}}
								>
									{summary.successfulRows} Imported
								</Tag>
							)}
							{hasErrors && (
								<Tag
									style={{
										background: '#f5f5f5',
										color: '#FF0000',
										border: 'none',
									}}
								>
									{summary.errorRows} Failed
								</Tag>
							)}
							<Tag
								style={{
									background: '#f5f5f5',
									color: '#584495',
									border: 'none',
								}}
							>
								{summary.totalRows} Total Processed
							</Tag>
						</div>
					</div>

					{/* Success Preview */}
					{hasSuccess && (
						<div style={{ marginBottom: '16px' }}>
							<Title
								level={5}
								style={{ color: '#009049', margin: '0 0 8px' }}
							>
								Successfully Imported ({summary.successfulRows}{' '}
								earnings)
							</Title>
							<div
								style={{
									background: '#fafafa',
									border: '1px solid #f0f0f0',
									borderRadius: '6px',
									padding: '12px',
									fontSize: '14px',
								}}
							>
								{summary.successSample?.map(
									(item: any, index: number) => (
										<>
											<div
												key={index}
												style={{
													marginBottom: '6px',
													display: 'flex',
													justifyContent:
														'space-between',
												}}
											>
												<div>{item.employeeName}</div>
												<div
													style={{
														color: '#666',
														fontSize: '12px',
														marginTop: '2px',
													}}
												>
													Earning Code:{' '}
													{item.earningCode} | Amount:{' '}
													{item.amount}
												</div>
											</div>
											{index <
												summary.successSample.length -
													1 && (
												<Divider
													style={{ margin: '8px 0' }}
												/>
											)}
										</>
									)
								)}
								{summary.hasMoreSuccess && (
									<div
										style={{
											fontStyle: 'italic',
											color: '#666',
											marginTop: '8px',
											textAlign: 'center',
											fontSize: '12px',
										}}
									>
										...and {summary.successfulRows - 5} more
										earnings added
									</div>
								)}
							</div>
						</div>
					)}

					{/* Errors Section */}
					{hasErrors && (
						<div style={{ marginBottom: '16px' }}>
							<Title
								level={5}
								style={{ color: '#FF0000', margin: '0 0 8px' }}
							>
								Failed Records ({summary.errorRows})
							</Title>
							<div
								style={{
									maxHeight: '200px',
									overflowY: 'auto',
									padding: '12px',
									background: '#fff',
									border: '1px solid #f0f0f0',
									borderRadius: '6px',
								}}
							>
								{errors.map((error: any, index: number) => (
									<div
										key={index}
										style={{
											marginBottom: '12px',
											padding: '8px 0',
											borderBottom:
												index < errors.length - 1
													? '1px solid #eee'
													: 'none',
										}}
									>
										<div
											style={{
												fontWeight: 'bold',
												fontSize: '13px',
												display: 'flex',
												justifyContent: 'space-between',
											}}
										>
											<span>Row {error.row}</span>
											<span
												style={{
													fontWeight: 'normal',
													color: '#666',
												}}
											>
												Earning Code:{' '}
												{error.data['Earning Code']} |
												NRC:{' '}
												{
													error.data[
														'National Registration Number'
													]
												}
											</span>
										</div>
										<div
											style={{
												fontSize: '12px',
												color: '#666',
												marginTop: '4px',
											}}
										>
											{error.errors.join(' • ')}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Action Buttons */}
					<div
						style={{
							display: 'flex',
							gap: '12px',
							justifyContent: 'flex-start',
							paddingTop: '16px',
							borderTop: '1px solid #f0f0f0',
						}}
					>
						{hasErrors && (
							<Button onClick={resetImport}>
								Import Another File
							</Button>
						)}

						{hasErrors && (
							<Button
								icon={<ExportOutlined />}
								onClick={() => {
									const errorCsv = errors
										.map(
											(e: any) =>
												`"${e.row}","${
													e.data['Earning Code']
												}","${
													e.data[
														'National Registration Number'
													]
												}","${e.errors.join('; ')}"`
										)
										.join('\n');

									const blob = new Blob(
										[
											`"Row","Earning Code","National Registration Number","Errors"\n${errorCsv}`,
										],
										{ type: 'text/csv' }
									);

									const url =
										window.URL.createObjectURL(blob);
									const link = document.createElement('a');
									link.href = url;
									link.download = `earnings_import_errors_${
										new Date().toISOString().split('T')[0]
									}.csv`;
									link.click();
									window.URL.revokeObjectURL(url);

									toastText(
										'Error report downloaded',
										'success'
									);
								}}
							>
								Download Error Report
							</Button>
						)}

						<Button
							type="primary"
							style={{ background: '#584495' }}
							onClick={closeAndRefresh}
						>
							{hasSuccess ? 'Done' : 'Close'}
						</Button>
					</div>

					{/* Bottom Info */}
					{hasSuccess && (
						<div
							style={{
								marginTop: '12px',
								padding: '8px 12px',
								background: '#fafafa',
								border: '1px solid #f0f0f0',
								borderRadius: '6px',
								fontSize: '12px',
								color: '#584495',
								textAlign: 'center',
							}}
						>
							💡 The imported earnings have been added to your
							payroll earnings.
						</div>
					)}
				</div>
			);
		}

		// Original upload UI
		return (
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<Title level={4}>Upload Earnings</Title>
					<div className={styles['right-align-items']}>
						<div>
							<p
								className={styles['template-link']}
								onClick={downloadTemplate}
							>
								<DownloadSvg />
								Download Template
							</p>
						</div>
						<div
							className={styles['close-icon']}
							onClick={cancelUploadEarningsModal}
						>
							<CloseOutlined />
						</div>
					</div>
				</div>
				<div>
					<div className={styles['modalUploadContainer']}>
						<Form.Item name="file">
							<Dragger {...propsUpload} className="test">
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-hint">
									Drag and drop a file or choose file from
									Device Supported file formats.
									<br />
								Single XLSX |{' '}
									<span className="color-purple">
										Maximum file size: 5MB
									</span>
									<br />
									<span className="color-purple" style={{ fontSize: '14px' }}>
										Maximum 200 records only
									</span>
								</p>
							</Dragger>
						</Form.Item>
					</div>

					<div className={styles['buttons']}>
						<Buttons buttons={uploadButtons} />
					</div>
				</div>
			</div>
		);
	};

	const handleFileSubmit = async () => {
		if (!file) {
			toastText('Please select xlsx file', 'error');
			return;
		}
		try {
			await handleUpload(file);
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				'Something went wrong in uploading xlsx';
			toastText(message, 'error');
		}
	};

	const uploadButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isUploadLoading,
			onclick: () => {
				cancelUploadEarningsModal();
			},
			minWidth: '10rem',
		},
		{
			text: 'Upload',
			isLoading: isUploadLoading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: isUploadLoading,
			onclick: () => {
				handleFileSubmit();
			},
			minWidth: '10rem',
		},
	];

	const propsUpload = {
		name: 'file',
		accept: '.xlsx',
		maxCount: 1,
		fileList: fileList,
		beforeUpload: (file: UploadFile) => {
			if (!file) {
				return;
			}

			const isXLSX =
				file.type ===
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
				file.name.endsWith('.xlsx');
			if (!isXLSX) {
				toastText('Only XLSX files are allowed', 'error');
				return Upload.LIST_IGNORE;
			}

			const isLt1M = file.size! / 1024 / 1024 < 5;
			if (!isLt1M) {
				toastText('File must be smaller than 5MB', 'error');
				return Upload.LIST_IGNORE;
			}

			setFile(file);
			setFileList([file]);
			return false;
		},
		onChange(info: UploadChangeParam<UploadFile>) {
			const { file } = info;
			if (!isRemoving) {
				setFile(file);
				setFileList([file]);
			} else {
				isRemoving = false;
				setFile(null);
				setFileList([]);
			}
		},
		onDrop: () => {
			setFile(null);
			setFileList([]);
		},
		onRemove: () => {
			isRemoving = true;
		},
	};

	const cancelUploadEarningsModal = () => {
		setFile(null);
		setFileList([]);
		setIsUploadEarningsModalVisible(false);
		setIsLoading(false);
		fetchEarnings(selectedPayrollId);
		setShowResults(false);
		setImportResults(null);
	};

	const showUploadEarningsModal = () => {
		setIsUploadEarningsModalVisible(true);
	};

	return (
		<div className="payroll-steps-table">
			<GlobalHeader
				headerData={headerData}
				toggleRequired={true}
				fetchAllData={() => fetchEarnings(selectedPayrollId)}
				isLoading={isEarningsLoading}
			/>
			<div
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					marginBottom: '4px',
					fontSize: '14px',
				}}
			>
				{/* Left side - Statutory Components toggle */}

				{/* Right side - Action buttons */}
				{!onlyView && (
					<div style={{ display: 'flex', gap: '8px' }}>
						<Button
							type="primary"
							onClick={() => {
								setIsAddEarningModal(true);
								fetchEarningOptions(selectedPayrollId);
							}}
							disabled={isEarningsLoading}
						>
							Add Earning
						</Button>
						<Button
							type="primary"
							onClick={showUploadEarningsModal}
							disabled={isEarningsLoading}
						>
							Upload Earnings
						</Button>
					</div>
				)}
			</div>
			<Table
				className="total-earning-table"
				columns={columns}
				dataSource={earningsData}
				rowClassName={(record, index) =>
					index === earningsData.length - 1
						? 'sticky-row  color-purple font-bold'
						: ''
				}
				bordered
				scroll={{
					y: showSteps
						? 'calc(100vh - 455px)'
						: 'calc(100vh - 375px)',
				}}
				pagination={{
					total: totalRecords,
					current: page,
					pageSize: pageSize,
					showSizeChanger: false,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				rowKey={(record: any) => record.id}
				loading={isEarningsLoading}
				onChange={tableChangeHandler}
			/>

			{isAddEarningModal && (
				<Modal
					open={isAddEarningModal}
					onOk={() => {}}
					onCancel={closeEarningModal}
					okText={'Save'}
					centered
					closable={false}
					width={650}
					footer={null}
					title={<h2>Add Earning</h2>}
					rootClassName="addEarningModalRoot"
				>
					<p className={styles['form-container-head-warning']}>
						<b>
							{' '}
							<sup>*</sup>
						</b>{' '}
						Indicated mandatory fields
					</p>
					<div
						style={{
							marginTop: '12px',
							padding: '12px 16px',
							backgroundColor: '#f6f4ff', // Light purple background
							borderRadius: '6px',
							fontSize: '14px',
							color: '#584495', // Main purple text color
							display: 'flex',
							alignItems: 'flex-start',
							gap: '4px',
						}}
					>
						<div>
							<strong style={{ color: '#584495' }}>Note: </strong>
							Any Earning added will be applied to this payroll
							only.
						</div>
					</div>
					<Row className={styles['form-container-card']} gutter={50}>
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<SelectDropdown
								placeholder="Employee Name"
								options={employeeOptions}
								value={earningDetails?.employeeId}
								onChange={(value: any) =>
									handleChange(value, 'employeeId', true)
								}
								size="large"
								required={true}
								helperText="Employee Name required"
								label="Employee Code | Employee Name"
								isError={formError.employeeId}
								loading={isEarningOptionsLoading}
							/>
						</Col>{' '}
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<SelectDropdown
								placeholder="Earning Type"
								options={
									earningDetails?.employeeId
										? earningsOptions[
												earningDetails.employeeId
										  ] || []
										: []
								}
								value={earningDetails?.earningType}
								disabled={invalidText(
									earningDetails?.employeeId
								)}
								onChange={(value: any) =>
									handleChange(value, 'earningType', true)
								}
								size="large"
								required={true}
								helperText="Earning Type required"
								label="Earning Type"
								isError={formError.earningType}
								loading={isEarningOptionsLoading}
							/>
						</Col>
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<InputNumberField
								name="amount"
								value={earningDetails.amount}
								label="Amount"
								required={true}
								helperText="Amount is required"
								onChange={(value) =>
									handleChange(value, 'amount', true)
								}
								// suffix={'ZMW'}
								isError={formError.amount}
							/>
						</Col>
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<TextareaFieldInput
								name="Reason"
								value={earningDetails.notes}
								label="Reason"
								required={true}
								helperText="Reason is required"
								onChange={(value) =>
									handleChange(value, 'notes', true)
								}
								isError={formError.notes}
							/>
						</Col>
					</Row>

					<div className={styles['buttons']}>
						<Buttons buttons={myButtons} />
					</div>
				</Modal>
			)}
			{isUploadEarningsModalVisible && (
				<Modal
					open={isUploadEarningsModalVisible}
					maskClosable={!showResults} // Prevent accidental close when showing results
					width={showResults ? '800px' : '600px'} // Wider for results
					footer={null}
					closable={false}
					onCancel={cancelUploadEarningsModal}
					centered
				>
					{renderModalContent()}
				</Modal>
			)}
		</div>
	);
};

export default TotalEarningTable;
